import React from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { FlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { Field } from '@features/query/form/Field';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;
const ActionsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;
const PrimaryAction = styled.div`
  flex: 1 1 200px;
`;
const MoreItersAction = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
const FollowUpTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 8px;
  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.themeColors.text};
  resize: vertical;
`;
const ItersOverrideInput = styled.input.attrs({ type: 'number', min: 1 })`
  width: 64px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.themeColors.text};
  background: ${({ theme }) => theme.themeColors.appBackground};
`;
const HelpText = styled.div`
  padding-top: 4px;
`;

const FOLLOW_UP_PLACEHOLDER = 'Ask a follow-up question — or use "Run more iterations" to extend the prior question.';

export const FollowUpInput = ({ disabled = false, disabledReason = null, onAskFollowUp, onRunMoreIterations }) => {
  const [question, setQuestion] = React.useState('');
  const [itersOverride, setItersOverride] = React.useState('');

  const handleChange = (e) => setQuestion(e.target.value);
  const handleItersChange = (e) => setItersOverride(e.target.value);

  const trimmedQuestion = question.trim();
  const canAsk = !disabled && trimmedQuestion.length > 0;
  const canRunMore = !disabled;

  const parsedIters = (() => {
    const n = parseInt(itersOverride, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const handleAsk = () => {
    if (!canAsk) return;
    onAskFollowUp(trimmedQuestion);
    setQuestion('');
  };

  const handleRunMore = () => {
    if (!canRunMore) return;
    onRunMoreIterations(parsedIters);
    setItersOverride('');
  };

  return (
    <Card>
      <Field
        label="Follow-up"
        component={
          <FollowUpTextarea
            id="follow-up-input"
            value={question}
            onChange={handleChange}
            placeholder={FOLLOW_UP_PLACEHOLDER}
            disabled={disabled}
          />
        }
      />
      <ActionsRow>
        <PrimaryAction>
          <FlatButton fullWidth onClick={handleAsk} disabled={!canAsk}>
            Ask Follow-Up
          </FlatButton>
        </PrimaryAction>
        <MoreItersAction>
          <SkeletonButton onClick={handleRunMore} disabled={!canRunMore}>
            Run More Iterations
          </SkeletonButton>
          <Body5 isLowContrast>+</Body5>
          <ItersOverrideInput
            value={itersOverride}
            onChange={handleItersChange}
            disabled={disabled}
            placeholder=""
            aria-label="Additional iterations override"
          />
          <Body5 isLowContrast>iters</Body5>
        </MoreItersAction>
      </ActionsRow>
      {disabled && disabledReason ? (
        <HelpText>
          <Body5 isLowContrast>{disabledReason}</Body5>
        </HelpText>
      ) : null}
    </Card>
  );
};
